// @flow
import client from '../../client'
import { config } from '../../config'
import APIError from '../../client/APIError'
import ONDEMAND_STATUS from '../../constants/ondemandStatus'
import prebookReservationBuilder from './builders/prebook'
import ondemandReservationBuilder from './builders/ondemand'
import format from '../../utils/format'
import poller from '../../utils/poller'
import { filterReserveQuery } from './utils'
import ReservationError, { ERROR_CODES } from './ReservationError'

// Types
import type { Reservation } from '../../models/reservation'

export type Data = Object // FIXME

// Constants
export const CREATE_RESERVATION_ENDPOINT = '/reservations/'
export const POLL_RESERVATION_ENDPOINT = '/reservations/{0}/poll/'
export const ONDEMAND_CREATE_RESERVATION_ENDPOINT = '/ondemand/reservations/'
export const ONDEMAND_POLL_RESERVATION_ENDPOINT = '/ondemand/reservations/{0}/'

// Utils
const isOndemandFinal = (status: string) => {
  switch (status) {
    case ONDEMAND_STATUS.FAILED:
    case ONDEMAND_STATUS.LOST:
    case ONDEMAND_STATUS.NO_DRIVER:
    case ONDEMAND_STATUS.PAYMENT_AUTH_FAILED:
    case ONDEMAND_STATUS.CANCELLED_BY_USER:
    case ONDEMAND_STATUS.CANCELLED_BY_DRIVER:
    case ONDEMAND_STATUS.COMPLETED:
    case ONDEMAND_STATUS.PAID:
    case ONDEMAND_STATUS.PAID_THROUGH_PROVIDER:
      return true
    default:
      return false
  }
}

const processFinalResult = (result) => {
  if (!result) throw new ReservationError(ERROR_CODES.unable_create_reservation)
  return result
}

/**
 * Create a reservation
 * @see {@link https://api-staging-internal.mozio.com/v2/docs/#!/reservations/Reservation_create|Create reservation}
 * @param {Object} data Reservation information
 * @param {string} data.trip_id Trip ID
 * @param {string} data.search_id Session ID
 * @param {string} data.first_name First name
 * @param {string} data.last_name Last name
 * @param {string} data.email Email
 * @param {string} data.phone_number Phone number
 * @param {string} [data.currency] Currency
 * @param {string} [data.provider] Provider
 * @param {string} [data.airline] Airline, required if going from the airport
 * @param {string} [data.flight_number] Flight number, required if going from the airport
 * @param {string} [data.pickup_instructions] Pickup instructions, Instructions to help the driver find the customer
 * @param {string} [data.customer_special_instructions] Special instructions given by the customer
 * @param {string} [data.partner_tracking_id] Partner number, they use to track the booking in their own system
 * @param {string} [data.country_code_name] Country code name
 * @param {string} [data.alternative_time_index]
 * @param {number} [data.return_alternative_time_index]
 * @param {number} [data.return_alternative_time_index]
 * @param {string} [data.return_airline] Airline for the return trip
 * @param {string} [data.airline_iata_code] Airline IATA code
 * @param {string} [data.return_airline_iata_code] Airline IATA code of the return flight
 * @param {string} [data.return_flight_number] Return flight number
 * @param {string} [data.coupon_code] A valid coupon code
 * @param {string} [data.password] Password submited by the user to create an account
 * @param {string} [data.card_source_id] Stripe card_source_id, required if stored_card_id is not sent
 * @param {string} [data.stored_card_id] Card id stored in mozio-auth. required if card_source_id is not sent
 * @param {boolean} [data.save_card] Whether card information should be saved or not
 * @param {string} [data.description] Reservation description
 * @param {number} [data.gratuity_percent] Gratuity percent
 * @param {number} [data.gratuity_amount] Gratuity amount
 * @param {boolean} [data.meet_and_greet] Whether or not choose meet and greet
 * @param {number} [data.commission] Travel agent commision
 * @param {Array<string>} [data.extra_pax_info] Personal information of the extra passengers
 * @param {boolean} [data.include_cancellations] Include cancelled reservations in the response
 * @param {string} [data.selected_ticket_id] Selected ticket type
 * @param {string} [data.return_selected_ticket_id] Selected ticket type for the return
 * @param {Array<string>} [data.optional_amenities] Optional amenities selected, like wifi or wheelchair
 * @param {string} [data.create_ta_profile] Whether or not we want to create a TA customer profile
 * @param {string} [data.ta_email] Extra email used by travel agents
 * @return {Promise|Error} A resolved Promise with the reservation created or Rejected with an Error
 */
export const createPrebook = (data: Data): Promise<Reservation[]> => {
  const query = filterReserveQuery(data)
  const pollEndpoint = format(POLL_RESERVATION_ENDPOINT, query.search_id)
  const createEndpoint = CREATE_RESERVATION_ENDPOINT
  const builder = prebookReservationBuilder

  const createPoller = poller({
    initPoll: () =>
      client.post(createEndpoint, {
        body: query,
        baseUrl: config.BASE_URL_RESERVATIONS,
      }),
    pollNext: () =>
      client.get(pollEndpoint, { baseUrl: config.BASE_URL_RESERVATIONS }),
    builder: (res) => res.reservations.map(builder),
    isMoreComing: (res) => res.status === 'pending',
    isResultEmpty: (res) =>
      !res || !res.reservations || !res.reservations.length,
    isError: (res) =>
      res &&
      res.status === 'trip_price_changed' &&
      new APIError('', res.status, 400),
    pollAll: true,
  })

  return createPoller().then(processFinalResult)
}

/**
 * Creates the ondemand reservation
 * @type {Function}
 */
export const createOndemand = (data: Data): Promise<Reservation[]> => {
  const query = filterReserveQuery(data)
  const createEndpoint = ONDEMAND_CREATE_RESERVATION_ENDPOINT

  const createPoller = poller({
    initPoll: () => client.post(createEndpoint, { body: query }),
    pollNext: (id) =>
      client.get(format(ONDEMAND_POLL_RESERVATION_ENDPOINT, id)),
    builder: (res) => [ondemandReservationBuilder(res)],
    isMoreComing: (res) => res.status === ONDEMAND_STATUS.PENDING,
    isResultEmpty: (res) =>
      !res || !res.status || res.status === ONDEMAND_STATUS.PENDING,
    isError: (res) =>
      isOndemandFinal(res && res.status) && new APIError('', res.status, 400),
    pollAll: true,
  })

  return createPoller().then(processFinalResult)
}

const create = (data: Data): Promise<Reservation[]> =>
  data.ondemand ? createOndemand(data) : createPrebook(data)

export default create
